import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * MG3P Loading State — skeleton unificado para cards, listas e tabelas.
 */
@Component({
  selector: 'mg3p-loading-state',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="mg3p-loading" [class]="'mg3p-loading--' + type" role="status" aria-live="polite">
      @if (type === 'skeleton') {
        <div class="mg3p-loading__skeleton-container">
          @for (i of skeletonArray; track i) {
            <div class="mg3p-loading__skeleton-card">
              <div class="mg3p-loading__skeleton-header"></div>
              <div class="mg3p-loading__skeleton-line"></div>
              <div class="mg3p-loading__skeleton-line mg3p-loading__skeleton-line--short"></div>
            </div>
          }
        </div>
        <span class="sr-only">{{ message || 'Carregando conteúdo...' }}</span>
      }

      @if (type === 'list') {
        <div class="mg3p-loading__list" [attr.aria-label]="message || 'Carregando lista'">
          @for (i of skeletonArray; track i) {
            <div class="mg3p-loading__list-item">
              <div class="mg3p-loading__list-line mg3p-loading__list-line--title"></div>
              <div class="mg3p-loading__list-line mg3p-loading__list-line--meta"></div>
            </div>
          }
        </div>
        <span class="sr-only">{{ message || 'Carregando lista...' }}</span>
      }

      @if (type === 'table') {
        <div class="mg3p-loading__table-wrap" [attr.aria-label]="message || 'Carregando tabela'">
          <div class="mg3p-loading__table-head">
            @for (c of columnArray; track c) {
              <div class="mg3p-loading__table-cell mg3p-loading__table-cell--head"></div>
            }
          </div>
          @for (r of skeletonArray; track r) {
            <div class="mg3p-loading__table-row">
              @for (c of columnArray; track c) {
                <div class="mg3p-loading__table-cell"></div>
              }
            </div>
          }
        </div>
        <span class="sr-only">{{ message || 'Carregando tabela...' }}</span>
      }

      @if (type === 'spinner' || type === 'inline') {
        <div class="mg3p-loading__spinner-container">
          <div
            class="mg3p-loading__spinner"
            [class]="'mg3p-loading__spinner--' + size"
            aria-hidden="true"
          ></div>
          @if (message && type === 'spinner') {
            <p class="mg3p-loading__message">{{ message }}</p>
          }
          <span class="sr-only">{{ message || 'Carregando...' }}</span>
        </div>
      }

      @if (type === 'progress' && progress !== undefined) {
        <div class="mg3p-loading__progress-container">
          <div
            class="mg3p-loading__progress-bar"
            role="progressbar"
            [attr.aria-valuenow]="progress"
            [attr.aria-valuemin]="0"
            [attr.aria-valuemax]="100"
          >
            <div class="mg3p-loading__progress-fill" [style.width.%]="progress"></div>
          </div>
          @if (message) {
            <p class="mg3p-loading__message">{{ message }}</p>
          }
          <span class="sr-only">{{ progress }}% concluído</span>
        </div>
      }
    </div>
  `,
  styles: [`
    .mg3p-loading {
      display: block;
      width: 100%;
    }

    .mg3p-loading__skeleton-container {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: var(--space-md);
    }

    .mg3p-loading__skeleton-card {
      padding: var(--space-md);
      border: 1px solid var(--color-border-light);
      border-radius: var(--radius-lg);
      background: var(--color-bg-primary);
    }

    .mg3p-loading__skeleton-header,
    .mg3p-loading__skeleton-line,
    .mg3p-loading__list-line,
    .mg3p-loading__table-cell {
      background: linear-gradient(
        90deg,
        var(--color-neutral-200) 25%,
        var(--color-neutral-100) 50%,
        var(--color-neutral-200) 75%
      );
      background-size: 200% 100%;
      border-radius: var(--radius-sm);
      animation: skeleton-shimmer 1.5s ease-in-out infinite;
    }

    .mg3p-loading__skeleton-header {
      height: 24px;
      width: 60%;
      margin-bottom: var(--space-md);
    }

    .mg3p-loading__skeleton-line {
      height: 16px;
      margin-bottom: var(--space-sm);
    }

    .mg3p-loading__skeleton-line--short {
      width: 40%;
    }

    .mg3p-loading__list {
      display: flex;
      flex-direction: column;
      gap: var(--space-sm);
    }

    .mg3p-loading__list-item {
      padding: var(--space-sm) var(--space-md);
      border: 1px solid var(--color-border-light);
      border-radius: var(--radius-md);
      background: var(--color-bg-primary);
    }

    .mg3p-loading__list-line--title {
      height: 18px;
      width: 72%;
      margin-bottom: var(--space-xs);
    }

    .mg3p-loading__list-line--meta {
      height: 14px;
      width: 48%;
    }

    .mg3p-loading__table-wrap {
      border: 1px solid var(--color-border-light);
      border-radius: var(--radius-lg);
      overflow: hidden;
      background: var(--color-bg-primary);
    }

    .mg3p-loading__table-head,
    .mg3p-loading__table-row {
      display: grid;
      grid-template-columns: repeat(var(--loading-cols, 3), 1fr);
      gap: var(--space-sm);
      padding: var(--space-sm) var(--space-md);
    }

    .mg3p-loading__table-head {
      background: var(--color-bg-elevated);
      border-bottom: 1px solid var(--color-border-light);
    }

    .mg3p-loading__table-row:not(:last-child) {
      border-bottom: 1px solid var(--color-border-light);
    }

    .mg3p-loading__table-cell {
      height: 16px;
    }

    .mg3p-loading__table-cell--head {
      height: 14px;
      opacity: 0.85;
    }

    @keyframes skeleton-shimmer {
      0% { background-position: 100% 0; }
      100% { background-position: -100% 0; }
    }

    .mg3p-loading__spinner-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: var(--space-md);
      padding: var(--space-xl);
    }

    .mg3p-loading--inline .mg3p-loading__spinner-container {
      flex-direction: row;
      padding: 0;
    }

    .mg3p-loading__spinner {
      border: 3px solid var(--color-pmdf-border);
      border-top-color: var(--color-pmdf-primary);
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }

    .mg3p-loading__spinner--sm { width: 20px; height: 20px; border-width: 2px; }
    .mg3p-loading__spinner--md { width: 40px; height: 40px; border-width: 3px; }
    .mg3p-loading__spinner--lg { width: 60px; height: 60px; border-width: 4px; }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    .mg3p-loading__message {
      margin: 0;
      font-size: var(--font-size-base);
      color: var(--color-text-secondary);
      text-align: center;
    }

    .mg3p-loading__progress-container {
      padding: var(--space-md);
    }

    .mg3p-loading__progress-bar {
      width: 100%;
      height: 8px;
      background: var(--color-pmdf-border);
      border-radius: var(--radius-full);
      overflow: hidden;
      margin-bottom: var(--space-sm);
    }

    .mg3p-loading__progress-fill {
      height: 100%;
      background: var(--color-pmdf-primary);
      border-radius: var(--radius-full);
      transition: width var(--transition-base);
    }

    @media (prefers-reduced-motion: reduce) {
      .mg3p-loading__skeleton-header,
      .mg3p-loading__skeleton-line,
      .mg3p-loading__list-line,
      .mg3p-loading__table-cell,
      .mg3p-loading__spinner,
      .mg3p-loading__progress-fill {
        animation: none;
        transition: none;
      }
    }
  `],
  host: {
    '[style.--loading-cols]': 'columns',
  },
})
export class LoadingStateComponent {
  @Input() type: 'skeleton' | 'spinner' | 'inline' | 'progress' | 'table' | 'list' = 'spinner';
  @Input() size: 'sm' | 'md' | 'lg' = 'md';
  @Input() message?: string;
  /** Linhas skeleton (cards, lista ou tabela). */
  @Input() count = 3;
  /** Colunas skeleton (apenas type="table"). */
  @Input() columns = 3;
  @Input() progress?: number;

  get skeletonArray(): number[] {
    return Array.from({ length: this.count }, (_, i) => i);
  }

  get columnArray(): number[] {
    return Array.from({ length: this.columns }, (_, i) => i);
  }
}
